import { useMemo } from 'react'
import PropTypes from 'prop-types'
import { answerPropType } from '@aytm/app/stats/page/question-views/components/lib/prop-types'

import ChartContainer from '@aytm/app/stats/page/question-views/components/charts/highcharts/ChartContainer'
import { useChartAxisApi } from '@aytm/app/stats/page/question-views/components/charts/highcharts/hooks/useChartAxisApi'

import {
  getChartModeTypes,
  getDefaultChartMode
} from '@aytm/app/stats/page/question-views/answer-variants/components/chart/lib/chartModes'
import { getTraitValues } from '@aytm/app/stats/page/question-views/answer-variants/components/chart/lib/traitValues'
import { splitChartVariants } from '@aytm/app/stats/page/question-views/mx/lib/summary/columns'

const AnswerVariantsChart = ({
  questionId,
  questionKey,
  qType,
  answerVariants,
  answer,
  sortOrder,
  chartMode: chartModeProp,
  // the mode the question declares in its data: drawn when the reader has picked none
  questionChartMode,
  globalChartMode,
  onChartModeChange,
  zoomLevel,
  globalZoomLevel,
  onZoomChange,
  zoomType,
  isRType,
  // the counting mode itself: the chart config picks response shares, intervals and the axis noun on it
  rType,
  isTraitType,
  traitName,
  trait,
  format,
  // root-format flags, derived coffee-side (renderReactChart) — the `format` prop
  // here is chart-scoped (format.chart) and does not contain them
  percentsOnly = false,
  ignoreAmount = false,
  ignorePercents = false,
  ignoreTraits = false,
  ignoreRefs = false,
  aType,
  answerType,
  answerLabels,
  unitLabel,
  viewMode,
  plotLines,
  degree,
  scaleTmpl,
  scalePow,
  scaleSteps,
  colorTheme,
  chartLegendMode,
  confidenceLevel,
  significanceLevel,
  confidenceLevels,
  features,
  metricsControl,
  onChartReady
}) => {
  const chartRef = useChartAxisApi(onChartReady)

  const chartModeTypes = useMemo(
    () =>
      getChartModeTypes({
        answer,
        percentsOnly,
        isRType,
        isTraitType,
        trait
      }),
    [answer, percentsOnly, isRType, isTraitType, trait]
  )

  const chartMode = useMemo(
    () =>
      getDefaultChartMode({
        chartMode: chartModeProp,
        questionChartMode,
        globalChartMode,
        answer,
        percentsOnly,
        isRType,
        isTraitType,
        trait
      }),
    [chartModeProp, questionChartMode, globalChartMode, answer, percentsOnly, isRType, isTraitType, trait]
  )

  const traitValues = useMemo(() => getTraitValues(trait), [trait])

  // the conditions the column builder splits under, asked here because the answer set has to be
  // settled before it is handed over: a split chart draws no summary column
  const isSplit = Boolean(trait) && !ignoreTraits && (undefined === questionKey || questionKey !== traitName)

  const drawnVariants = useMemo(
    () => splitChartVariants({ answerVariants, traitName: isSplit ? traitName : null, pie: 'pie' === chartMode }),
    [answerVariants, isSplit, traitName, chartMode]
  )

  const viewOptionsDisabled = features?.view_options?.disabled

  return (
    <ChartContainer
      chartRef={chartRef}
      questionId={questionId}
      questionKey={questionKey}
      qType={qType}
      answerVariants={drawnVariants}
      answer={answer}
      sortOrder={sortOrder}
      chartMode={chartMode}
      selectorChartModeValue={chartMode}
      chartModeOverride={chartModeProp}
      globalChartMode={globalChartMode}
      chartModeTypes={chartModeTypes}
      viewOptionsDisabled={viewOptionsDisabled}
      onChartModeChange={onChartModeChange}
      zoomLevel={zoomLevel}
      globalZoomLevel={globalZoomLevel}
      zoomType={zoomType && chartMode !== 'pie'}
      onZoomChange={onZoomChange}
      traitName={traitName}
      trait={trait}
      traitValues={traitValues}
      ignoreTraits={ignoreTraits}
      ignoreRefs={ignoreRefs}
      ignoreAmount={ignoreAmount}
      ignorePercents={ignorePercents}
      aType={aType}
      answerType={answerType}
      rType={rType}
      answerLabels={answerLabels}
      unitLabel={unitLabel}
      viewMode={viewMode}
      plotLines={plotLines}
      degree={degree}
      scaleTmpl={scaleTmpl}
      scalePow={scalePow}
      scaleSteps={scaleSteps}
      colorTheme={colorTheme}
      chartLegendMode={chartLegendMode}
      format={format}
      confidenceLevel={confidenceLevel}
      significanceLevel={significanceLevel}
      confidenceLevels={confidenceLevels}
      features={features}
      metricsControl={metricsControl}
    />
  )
}

AnswerVariantsChart.propTypes = {
  questionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  questionKey: PropTypes.string,
  qType: PropTypes.string, // a matrix batch withholds it on purpose -- it charts by answer variant
  answerVariants: PropTypes.array,
  answer: answerPropType,
  sortOrder: PropTypes.string,
  chartMode: PropTypes.string,
  questionChartMode: PropTypes.string,
  globalChartMode: PropTypes.string,
  onChartModeChange: PropTypes.func.isRequired,
  zoomLevel: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  globalZoomLevel: PropTypes.string,
  onZoomChange: PropTypes.func,
  zoomType: PropTypes.bool,
  isRType: PropTypes.bool,
  rType: PropTypes.string,
  isTraitType: PropTypes.bool,
  traitName: PropTypes.string,
  trait: PropTypes.object,
  format: PropTypes.object,
  percentsOnly: PropTypes.bool,
  ignoreAmount: PropTypes.bool,
  ignorePercents: PropTypes.bool,
  ignoreTraits: PropTypes.bool,
  ignoreRefs: PropTypes.bool,
  aType: PropTypes.string,
  answerType: PropTypes.string,
  answerLabels: PropTypes.object,
  unitLabel: PropTypes.string,
  viewMode: PropTypes.string,
  plotLines: PropTypes.array,
  degree: PropTypes.number,
  scaleTmpl: PropTypes.string,
  scalePow: PropTypes.number,
  scaleSteps: PropTypes.number,
  colorTheme: PropTypes.array,
  chartLegendMode: PropTypes.string,
  confidenceLevel: PropTypes.number,
  significanceLevel: PropTypes.number,
  confidenceLevels: PropTypes.array,
  features: PropTypes.object,
  // the summary metrics selector, which leads the chart's control strip where the block offers one
  metricsControl: PropTypes.node,
  onChartReady: PropTypes.func
}

AnswerVariantsChart.displayName = 'AnswerVariantsChart'

export default AnswerVariantsChart
